<template>
  <nav class="app-navigation">
    <button
      ref="botaoMenu"
      type="button"
      class="app-navigation__menu-button"
      aria-label="Abrir menu"
      :aria-expanded="isMenuVisible"
      aria-controls="app-navigation-menu"
      data-cy="menuNavegacao"
      @click="isMenuVisible = !isMenuVisible"
    >
      <VueInlineSvg class="icon-button" :src="iconMenu" />
    </button>

    <div
      v-if="isMenuVisible"
      id="app-navigation-menu"
      ref="menu"
      class="app-navigation__menu"
      @click="handleMenu"
      @click.self="isMenuVisible = false"
    >
      <div class="app-navigation__menu__container">
        <div class="app-navigation__menu__user-section">
          <p v-t="{ path: 'appNavigation.saudacao', args: { primeiroNome } }" />
        </div>

        <div class="app-navigation__menu__links-container">
          <router-link
            v-t="'appNavigation.inicio'"
            class="app-navigation__menu__link"
            to="/"
            data-ua="click-nav-portal-inicio-logado"
            data-cy="dividaBotaoInicio"
          />
          <a
            v-if="urlAjuda"
            v-t="'appNavigation.meAjuda'"
            class="app-navigation__menu__link"
            target="_blank"
            :href="urlAjuda"
            rel="noopener"
            data-ua="click-nav-portal-ajuda"
            data-cy="dividaBotaoMeAjuda"
          />
        </div>

        <div class="app-navigation__menu__logout-button__wrapper">
          <router-link
            v-t="'appNavigation.sair'"
            class="app-navigation__menu__logout-button"
            to="/logout"
            @click="handleClickSair"
            data-ua="click-nav-sair"
            data-cy="dividaBotaoSair"
          />
        </div>
      </div>
    </div>

    <div class="app-navigation__navbar-links">
      <router-link
        v-t="'appNavigation.inicio'"
        class="app-navigation__navbar-link"
        to="/"
        data-ua="click-nav-portal-inicio-logado"
        data-cy="dividaBotaoInicio"
      />
      <a
        v-if="urlAjuda"
        v-t="'appNavigation.meAjuda'"
        class="app-navigation__navbar-link"
        target="_blank"
        :href="urlAjuda"
        rel="noopener"
        data-ua="click-nav-portal-ajuda"
        data-cy="dividaBotaoMeAjuda"
      />

      <div class="app-navigation__navbar__logout-button__wrapper">
        <router-link
          v-t="'appNavigation.sair'"
          class="app-navigation__navbar__logout-button"
          to="/logout"
          @click="handleClickSair"
          data-ua="click-nav-sair"
          data-cy="dividaBotaoSair"
        />
      </div>
    </div>
  </nav>
</template>

<script lang="ts">
import { defineComponent, nextTick, onBeforeUnmount, ref, watch } from 'vue';

import VueInlineSvg from 'vue-inline-svg';
import iconMenu from '@/assets/images/menu.svg';
import { acertoClickEmSair } from '@/modules/events/acerto';

import { sessionService } from '@/services';

export default defineComponent({
  components: { VueInlineSvg },
  setup() {
    const primeiroNome = sessionService.token ? sessionService.token.primeiroNome : 'Usuário';

    const isMenuVisible = ref(false);
    const botaoMenu = ref<HTMLButtonElement | null>(null);
    const menu = ref<HTMLElement | null>(null);

    function fecharComEsc(event: KeyboardEvent) {
      if (event.key === 'Escape') isMenuVisible.value = false;
    }

    /**
     * Drawer por teclado: ao abrir, o foco entra no primeiro link, senão o Tab seguiria pela página
     * escondida atrás do overlay; ao fechar (Esc, clique fora ou link), volta ao botão que o abriu.
     */
    watch(isMenuVisible, async aberto => {
      if (aberto) {
        document.addEventListener('keydown', fecharComEsc);
        await nextTick();
        menu.value?.querySelector<HTMLElement>('a, button')?.focus();
        return;
      }

      document.removeEventListener('keydown', fecharComEsc);
      botaoMenu.value?.focus();
    });

    onBeforeUnmount(() => document.removeEventListener('keydown', fecharComEsc));

    const urlAjuda = appSettings.VUE_APP_URL_AJUDA;

    const handleMenu = (event: MouseEvent) => {
      const { target } = event;
      if (target instanceof HTMLElement && target.tagName.toUpperCase() === 'A') {
        isMenuVisible.value = false;
      }
    };

    const handleClickSair = () => {
      acertoClickEmSair();
    };

    return {
      iconMenu,
      primeiroNome,
      isMenuVisible,
      botaoMenu,
      menu,
      urlAjuda,
      sessionService,

      handleMenu,
      handleClickSair,
    };
  },
});
</script>

<style lang="scss" scoped>
.app-navigation__menu-button {
  padding: 0 10px;
  margin-right: -10px;
  display: flex;
  align-items: center;
  background: none;
  border: none;
  .icon-button{
    height: 30px;

    :deep(circle) {
      fill: var(--button-menu-toggle);
      stroke: var(--button-menu-toggle);
    }
  }
}

/*
 * Cobre a tela inteira. O recorte anterior (`top: 50px`) assumia o header permanentemente colado no
 * topo da viewport — com o documento rolando o header sai de cena, e aquele vão deixaria uma faixa
 * transparente sobre o conteúdo.
 */
.app-navigation__menu {
  position: fixed;
  display: flex;
  flex-direction: column;
  justify-content: stretch;
  inset: 0;
  height: 100dvh;
  background-color: #0009;
  z-index: 100;
}

.app-navigation__menu__container {
  background-color: white;
  width: 320px;
  margin-left: auto;
  display: flex;
  flex-flow: column nowrap;
  justify-content: stretch;
  flex-grow: 1;
}

.app-navigation__menu__user-section {
  border-bottom: 2px solid #424b53;
  padding: 40px;
  display: flex;
  align-items: center;

  p {
    color: #424b53;
    position: relative;
    padding-left: 30px;
    font-weight: 600;
    font-size: 18px;

    &:before {
      content: "";
      display: block;
      position: absolute;
      top: 50%;
      left: 0;
      transform: translateY(-50%);
      width: 20px;
      height: 20px;
      background-image: url("@/assets/images/icon-user.svg");
      background-size: contain;
    }
  }
}

.app-navigation__menu__links-container {
  padding: 50px 40px;
  background-color: #fff;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.app-navigation__menu__link {
  color: #424b53;
  font-size: 16px;
  line-height: 16px;
  margin-bottom: 30px;
  text-decoration: none;
  font-weight: 600;

  &:active {
    text-decoration: underline;
  }
}

.app-navigation__menu__logout-button__wrapper {
  margin: 30px 40px;
}

.app-navigation__menu__logout-button {
  display: block;
  width: 100%;
  font-weight: 600;
  letter-spacing: 1px;
  padding: 8px;
  border-radius: 16px;
  font-size: 16px;
  line-height: 16px;
  text-decoration: none;
  text-align: center;
  color: var(--acerto-primary-color, #424b53);
  border: 1px solid var(--acerto-primary-color, #424b53);
  background-color: transparent;
  transition: .2s ease-in;

  &:hover {
    color: var(--acerto-btn-primary-text, var(--text-primary, #424b53));
    background-color: var(--acerto-btn-primary-bg, var(--acerto-primary-color, rgba(255, 255, 255, 0.25)));
    border-color: var(--acerto-btn-primary-border, var(--acerto-primary-color, #424b53));
  }
}

.app-navigation__navbar-links {
  display: none;
}

@include responsive($desktop) {
  .app-navigation__menu-button {
    display: none;
  }

  .app-navigation__menu {
    display: none;
  }

  .app-navigation__navbar-links {
    display: flex;
    align-items: baseline;
  }

  .app-navigation__navbar-link {
    color: var(--text-primary, #FFFFFF);
    text-decoration: none;
    font-weight: 500;
    position: relative;
    padding: 5px;
    transition: all 0.1s ease-in-out;

    &:not(:last-child) {
      margin-right: 2em;
    }

    @include responsive($desktop) {
      font-size: 16px;
    }

    &:after {
      content: "";
      display: block;
      position: absolute;
      left: 0;
      right: 0;
      bottom: 2px;
      height: 2px;
      background-color: transparent;
      transition: all .15s ease-in-out;
    }

    &:hover {
      transform: translateY(-2px);

      &:after {
        background-color: #fff;
        transform: translateY(4px);
        transition-delay: .1s;
      }
    }
  }

  .app-navigation__navbar__logout-button {
    color: var(--acerto-primary-color, #FFFFFF);
    border: 1px solid var(--acerto-primary-color, #FFFFFF);
    background-color: transparent;
    font-size: 16px;
    line-height: 16px;
    padding: 6px 18px;
    border-radius: 12px;
    text-decoration: none;
    transition: .2s ease-in;

    &:hover {
      color: var(--acerto-btn-primary-text, var(--text-primary, #FFFFFF));
      background-color: var(--acerto-btn-primary-bg, var(--acerto-primary-color, rgba(255, 255, 255, 0.25)));
      border-color: var(--acerto-btn-primary-border, var(--acerto-primary-color, #FFFFFF));
    }

    &:active {
      color: var(--acerto-btn-primary-text, var(--text-primary, #FFFFFF));
      background-color: var(--acerto-btn-primary-bg, var(--acerto-primary-color, rgba(255, 255, 255, 0.25)));
      border-color: var(--acerto-btn-primary-border, var(--acerto-primary-color, #FFFFFF));
    }
  }
}
</style>
